import MESSAGES from 'core/data-model/constants/bi-messages'
import { partnerApiUtils } from 'core/service-wrapper/partner-api/handler-request/service-utils'
import VALUES from 'core/data-model/constants/values'

const { toNumber, tryToNumber } = partnerApiUtils

// /////////////////////////////////////////////////////////////////////////////
// WHOLE_LIFE
// /////////////////////////////////////////////////////////////////////////////

/**
 * mapping_investment_benefit_for_default
 * @param policyValue {PolicyValueResponse}
 */
function mapping_investment_benefit_for_default(policyValue) {
  const investment_benefit = policyValue.basic.investment_benefit_value_table
  if (investment_benefit) {
    return {
      guaranteeBenefits: [
        {
          title: MESSAGES.INVESTMENT_BENEFITS_GUARANTEE_CASHDROP,
          guaranteeValue: tryToNumber(investment_benefit.guarantee_cash_drop_value),
          rates: [
            tryToNumber(investment_benefit.guarantee_cash_drop_rate_ex1),
            tryToNumber(investment_benefit.guarantee_cash_drop_rate_ex2),
            tryToNumber(investment_benefit.guarantee_cash_drop_rate_ex3),
          ],
        },
        {
          title: MESSAGES.INVESTMENT_BENEFITS_GUARANTEE_MATURITY,
          guaranteeValue: tryToNumber(investment_benefit.guarantee_maturity_value),
          rates: [
            tryToNumber(investment_benefit.guarantee_maturity_rate_ex1),
            tryToNumber(investment_benefit.guarantee_maturity_rate_ex2),
            tryToNumber(investment_benefit.guarantee_maturity_rate_ex3),
          ],
        },
        {
          title: MESSAGES.INVESTMENT_BENEFITS_GUARANTEE_TOTAL,
          guaranteeValue: tryToNumber(investment_benefit.guarantee_total_value),
          rates: [
            tryToNumber(investment_benefit.guarantee_total_rate_ex1),
            tryToNumber(investment_benefit.guarantee_total_rate_ex2),
            tryToNumber(investment_benefit.guarantee_total_rate_ex3),
          ],
        },
      ],
      nonGuaranteeBenefits: [
        {
          title: MESSAGES.INVESTMENT_BENEFITS_NONGUARANTEE_MATURITY,
          guaranteeValue: tryToNumber(investment_benefit.non_guarantee_maturity_value),
          rates: [
            tryToNumber(investment_benefit.non_guarantee_maturity_rate_ex1),
            tryToNumber(investment_benefit.non_guarantee_maturity_rate_ex2),
            tryToNumber(investment_benefit.non_guarantee_maturity_rate_ex3),
          ],
        },
        {
          title: MESSAGES.INVESTMENT_BENEFITS_NONGUARANTEE_TOTAL,
          guaranteeValue: tryToNumber(investment_benefit.non_guarantee_total_value),
          rates: [
            tryToNumber(investment_benefit.non_guarantee_total_rate_ex1),
            tryToNumber(investment_benefit.non_guarantee_total_rate_ex2),
            tryToNumber(investment_benefit.non_guarantee_total_rate_ex3),
          ],
        },
      ],
    }
  }
  return undefined
}

/**
 * mapping_investment_benefit_for_index_linked
 * @param policyValue {PolicyValueResponse}
 */
function mapping_investment_benefit_for_index_linked(policyValue, basicPlanCode) {
  const policyValueBasic = policyValue.basic
  if (policyValueBasic) {
    const totalCashDrop = tryToNumber(policyValueBasic.total_cash_drop)
    const maturityBenefit = tryToNumber(policyValueBasic.maturity_benefit)

    const summaryLowDividend = tryToNumber(policyValueBasic.summary_low_dividend)
    const summaryMidDividend = tryToNumber(policyValueBasic.summary_mid_dividend)
    const summaryHighDividend = tryToNumber(policyValueBasic.summary_high_dividend)

    const minDeathCoverage = tryToNumber(policyValueBasic.min_death_coverage)
    const cashDropFirstPart = tryToNumber(policyValueBasic.cash_drop_first_part)

    const getNonGuaranteeBenefits = () => {
      if (Object.values(VALUES.INDEX_LINKED_25_5_CODE).includes(basicPlanCode)) {
        return {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_NONGUARANTEE_25_5,
          rates: [
            tryToNumber(policyValueBasic.table[9].dividend_case_0) +
            tryToNumber(policyValueBasic.table[14].dividend_case_0) +
            tryToNumber(policyValueBasic.table[19].dividend_case_0),

            tryToNumber(policyValueBasic.table[9].dividend_case_1) +
            tryToNumber(policyValueBasic.table[14].dividend_case_1) +
            tryToNumber(policyValueBasic.table[19].dividend_case_1),

            tryToNumber(policyValueBasic.table[9].dividend_case_2) +
            tryToNumber(policyValueBasic.table[14].dividend_case_2) +
            tryToNumber(policyValueBasic.table[19].dividend_case_2),
          ],
        }
      } else if (Object.values(VALUES.INDEX_LINKED_15_8_CODE).includes(basicPlanCode)) {
        return {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_NONGUARANTEE_15_8,
          rates: [
            tryToNumber(policyValueBasic.table[9].dividend_case_0),
            tryToNumber(policyValueBasic.table[9].dividend_case_1),
            tryToNumber(policyValueBasic.table[9].dividend_case_2),
          ],
        }
      }
    }

    const benefitCashDrop = () => {
      if (Object.values(VALUES.INDEX_LINKED_25_5_CODE).includes(basicPlanCode)) {
        return {
          title: MESSAGES.INDEX_LINKED_BENEFIT_CASH_DROP_25_5,
          value: cashDropFirstPart,
        }
      } else if (Object.values(VALUES.INDEX_LINKED_15_8_CODE).includes(basicPlanCode)) {
        return {
          title: MESSAGES.INDEX_LINKED_BENEFIT_CASH_DROP_15_8,
          value: cashDropFirstPart,
        }
      }
    }

    const benefitMessage = () => {
      if (Object.values(VALUES.INDEX_LINKED_25_5_CODE).includes(basicPlanCode)) {
        return MESSAGES.INDEX_LINKED_MESSAGE.BENEFIT_SUMMARY_TABLE_2[5].benefit_25_5
      } else if (Object.values(VALUES.INDEX_LINKED_15_8_CODE).includes(basicPlanCode)) {
        return MESSAGES.INDEX_LINKED_MESSAGE.BENEFIT_SUMMARY_TABLE_2[5].benefit_15_8
      }
    }

    return {
      guaranteeBenefits: [
        {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_GUARANTEE_CASHDROP,
          rates: [totalCashDrop, totalCashDrop, totalCashDrop],
        },
        {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_GUARANTEE_MATURITY,
          rates: [maturityBenefit, maturityBenefit, maturityBenefit],
        },
        {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_GUARANTEE_TOTAL,
          rates: [totalCashDrop + maturityBenefit, totalCashDrop + maturityBenefit, totalCashDrop + maturityBenefit],
        },
      ],
      nonGuaranteeBenefits: [
        getNonGuaranteeBenefits(),
        {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_NONGUARANTEE_MATURITY,
          rates: [summaryLowDividend, summaryMidDividend, summaryHighDividend],
        },
        {
          title: MESSAGES.INDEX_LINKED_INVESTMENT_BENEFITS_NONGUARANTEE_TOTAL,
          rates: [
            totalCashDrop + maturityBenefit + getNonGuaranteeBenefits().rates[0] + summaryLowDividend,
            totalCashDrop + maturityBenefit + getNonGuaranteeBenefits().rates[1] + summaryMidDividend,
            totalCashDrop + maturityBenefit + getNonGuaranteeBenefits().rates[2] + summaryHighDividend,
          ],
        },
      ],
      benefitSummaryUniversalLink: [
        {
          title: MESSAGES.INDEX_LINKED_DEATH_BENEFIT_MINIMUM_TITLE,
          value: minDeathCoverage,
        },
        {
          title: MESSAGES.INDEX_LINKED_BENEFIT_CASH_DROP_TITLE,
          value: 0,
        },
        benefitCashDrop(),
        {
          title: MESSAGES.INDEX_LINKED_BENEFIT_CASH_DROP_MATURITY_TITLE,
          value: maturityBenefit,
        },
        {
          title: MESSAGES.INDEX_LINKED_DIVIDEND_TITLE,
          value: benefitMessage(),
        },
      ],
    }
  }
  return undefined
}

/**
 * mapping_policy_for_default
 * @param calRes {CalculateResponse}
 * @param policyValue {PolicyValueResponse}
 */
function mapping_policy_for_default(calRes, policyValue) {
  return {
    yearEndPolicyValues: Array.from(policyValue.basic.table).map((/** @type {Record<string, string>} */ policy) => {
      return {
        age: { value: tryToNumber(policy.age) },
        yearEndPolicy: policy.year,
        annualBasicPremium: tryToNumber(policy.basic_premium),
        annualRiderPremium: tryToNumber(policy.rider_premium),
        annualTotalPremium: tryToNumber(policy.total_premium),
        basicSumAssured: tryToNumber(policy.basic_sa),
        boostBenefit: tryToNumber(policy.death_by_boost_benefit),
        cumulativeBasicPremium: tryToNumber(policy.cumulative_basic_premium),
        cashDrop: tryToNumber(policy.cash_drop),
        sumCashDrop: tryToNumber(policy.sum_cash_drop),
        surrenderCash: tryToNumber(policy.surrender_cash),
        deathBenefit: tryToNumber(policy.death_benefit),
        totalBenefit: tryToNumber(policy.total_benefit),
        cumulativeDeathBenefit: tryToNumber(policy.cumulative_death_benefit),
        lowSavingCashDrop: tryToNumber(policy.low_saving_cash_drop),
        mediumSavingCashDrop: tryToNumber(policy.medium_saving_cash_drop),
        highSavingCashDrop: tryToNumber(policy.high_saving_cash_drop),
        lowInterest: tryToNumber(policy.low_interest),
        lowInterestDeposit: tryToNumber(policy.low_interest_deposit),
        midInterest: tryToNumber(policy.mid_interest),
        midInterestDeposit: tryToNumber(policy.mid_interest_deposit),
        highInterest: tryToNumber(policy.high_interest),
        highInterestDeposit: tryToNumber(policy.high_interest_deposit),
        totalLowDividend: tryToNumber(policy.total_low_dividend),
        totalMidDividend: tryToNumber(policy.total_mid_dividend),
        totalHighDividend: tryToNumber(policy.total_high_dividend),
      }
    }),
    expenseRatio: tryToNumber(policyValue.basic.expense_ratio),
    subtotalAnnualBasicPremium: tryToNumber(policyValue.basic.summary_basic_premium),
    subtotalAnnualRiderPremium: tryToNumber(policyValue.basic.summary_rider_premium),
    grandTotalPremium: tryToNumber(policyValue.basic.summary_total_premium),
    subtotalCashDrop: tryToNumber(policyValue.basic.summary_cash_drop),
    subtotalLowSavingCashDrop: tryToNumber(policyValue.basic.summary_low_saving_cash_drop),
    subtotalMediumSavingCashDrop: tryToNumber(policyValue.basic.summary_medium_saving_cash_drop),
    subtotalHighSavingCashDrop: tryToNumber(policyValue.basic.summary_high_saving_cash_drop),
  }
}

/**
 * mapping_policy_for_index_linked
 * @param calRes {CalculateResponse}
 * @param policyValue {PolicyValueResponse}
 */
function mapping_policy_for_index_linked(calRes, policyValue) {
  return {
    yearEndPolicyValues: Array.from(policyValue.basic.table).map((/** @type {Record<string, string>} */ policy) => {
      return {
        age: { value: policy.age },
        yearEndPolicy: policy.year,
        annualBasicPremium: policy.basic_premium,
        annualRiderPremium: policy.rider_premium,
        annualTotalPremium: policy.total_premium,
        cumulativeBasicPremium: policy.cumulative_basic_premium,
        cashDrop: policy.cash_drop,
        sumCashDrop: policy.sum_cash_drop,
        surrenderCash: policy.surrender_cash,
        deathBenefit: policy.death_benefit,
        totalBenefit: policy.total_benefit,
        cumulativeDeathBenefit: policy.cumulative_death_benefit,
        lowSavingCashDrop: policy.low_saving_cash_drop,
        mediumSavingCashDrop: policy.medium_saving_cash_drop,
        highSavingCashDrop: policy.high_saving_cash_drop,
        lowDividend: policy.dividend_case_0,
        mediumDividend: policy.dividend_case_1,
        highDividend: policy.dividend_case_2,
        lowSavingDividend: policy.saving_dividend_case_0,
        mediumSavingDividend: policy.saving_dividend_case_1,
        highSavingDividend: policy.saving_dividend_case_2,
        lowInterest: policy.low_interest,
        lowInterestDeposit: policy.low_interest_deposit,
        midInterest: policy.mid_interest,
        midInterestDeposit: policy.mid_interest_deposit,
        highInterest: policy.high_interest,
        highInterestDeposit: policy.high_interest_deposit,
        totalLowDividend: policy.total_low_dividend,
        totalMidDividend: policy.total_mid_dividend,
        totalHighDividend: policy.total_high_dividend,
        lowDeathBenefitPlusDividend: policy.death_benefit_plus_dividend_case_0,
        mediumDeathBenefitPlusDividend: policy.death_benefit_plus_dividend_case_1,
        highDeathBenefitPlusDividend: policy.death_benefit_plus_dividend_case_2,
        lowSurrenderCashPlusDividend: policy.surrender_cash_plus_dividend_case_0,
        mediumSurrenderCashPlusDividend: policy.surrender_cash_plus_dividend_case_1,
        highSurrenderCashPlusDividend: policy.surrender_cash_plus_dividend_case_2,
      }
    }),
    expenseRatio: tryToNumber(policyValue.basic.expense_ratio),
    lowFormulaYear4: getFormulaCaseData(policyValue.basic.formula_year_4, 0),
    mediumFormulaYear4: getFormulaCaseData(policyValue.basic.formula_year_4, 1),
    highFormulaYear4: getFormulaCaseData(policyValue.basic.formula_year_4, 2),
    lowFormulaYear7: getFormulaCaseData(policyValue.basic.formula_year_7, 0),
    mediumFormulaYear7: getFormulaCaseData(policyValue.basic.formula_year_7, 1),
    highFormulaYear7: getFormulaCaseData(policyValue.basic.formula_year_7, 2),
    lowFormulaYear9: getFormulaCaseData(policyValue.basic.formula_year_9, 0),
    mediumFormulaYear9: getFormulaCaseData(policyValue.basic.formula_year_9, 1),
    highFormulaYear9: getFormulaCaseData(policyValue.basic.formula_year_9, 2),
    lowFormulaYear10: getFormulaCaseData(policyValue.basic.formula_year_10, 0),
    mediumFormulaYear10: getFormulaCaseData(policyValue.basic.formula_year_10, 1),
    highFormulaYear10: getFormulaCaseData(policyValue.basic.formula_year_10, 2),
    lowFormulaYear15: getFormulaCaseData(policyValue.basic.formula_year_15, 0),
    mediumFormulaYear15: getFormulaCaseData(policyValue.basic.formula_year_15, 1),
    highFormulaYear15: getFormulaCaseData(policyValue.basic.formula_year_15, 2),
    lowFormulaYear17: getFormulaCaseData(policyValue.basic.formula_year_17, 0),
    mediumFormulaYear17: getFormulaCaseData(policyValue.basic.formula_year_17, 1),
    highFormulaYear17: getFormulaCaseData(policyValue.basic.formula_year_17, 2),
    lowFormulaYear20: getFormulaCaseData(policyValue.basic.formula_year_20, 0),
    mediumFormulaYear20: getFormulaCaseData(policyValue.basic.formula_year_20, 1),
    highFormulaYear20: getFormulaCaseData(policyValue.basic.formula_year_20, 2),
    lowFormulaYear25: getFormulaCaseData(policyValue.basic.formula_year_25, 0),
    mediumFormulaYear25: getFormulaCaseData(policyValue.basic.formula_year_25, 1),
    highFormulaYear25: getFormulaCaseData(policyValue.basic.formula_year_25, 2),
    subtotalAnnualBasicPremium: tryToNumber(policyValue.basic.summary_basic_premium),
    cumulativeBasicPremiumWhenDeath_25_5: tryToNumber(Array.from(policyValue.basic.table)[3].cumulative_basic_premium),
    cumulativeBasicPremiumWhenDeath_15_8: tryToNumber(Array.from(policyValue.basic.table)[6].cumulative_basic_premium),
    subtotalAnnualRiderPremium: tryToNumber(policyValue.basic.summary_rider_premium),
    grandTotalPremium: tryToNumber(policyValue.basic.summary_total_premium),
    subtotalCashDrop: tryToNumber(policyValue.basic.summary_cash_drop),
    subtotalLowSavingCashDrop: tryToNumber(policyValue.basic.summary_low_saving_cash_drop),
    subtotalMediumSavingCashDrop: tryToNumber(policyValue.basic.summary_medium_saving_cash_drop),
    subtotalHighSavingCashDrop: tryToNumber(policyValue.basic.summary_high_saving_cash_drop),
    subtotalLowSumDividend: tryToNumber(policyValue.basic.summary_sum_dividend_case_0),
    subtotalMediumSumDividend: tryToNumber(policyValue.basic.summary_sum_dividend_case_1),
    subtotalHighSumDividend: tryToNumber(policyValue.basic.summary_sum_dividend_case_2),
    subtotalLowSumSavingDividend: tryToNumber(policyValue.basic.summary_saving_dividend_case_0),
    subtotalMediumSumSavingDividend: tryToNumber(policyValue.basic.summary_saving_dividend_case_1),
    subtotalHighSumSavingDividend: tryToNumber(policyValue.basic.summary_saving_dividend_case_2),
    lowIRR: policyValue.basic.internal_rate_of_return_0,
    mediumIRR: policyValue.basic.internal_rate_of_return_1,
    highIRR: policyValue.basic.internal_rate_of_return_2,
  }
}

function getFormulaCaseData(data, caseIndex) {
  try {
    return {
      index: tryToNumber(data[`index_case_${caseIndex}`]),
      summaryDividend: tryToNumber(data[`summary_dividend_case_${caseIndex}`]),
      summaryDividendWithIndex: tryToNumber(data[`summary_dividend_with_index_case_${caseIndex}`]),
    }
  } catch {
    return {
      index: 0,
      summaryDividend: 0,
      summaryDividendWithIndex: 0,
    }
  }
}

/**
 * mapping_policy_for_bumnan_ready
 * @param calRes {CalculateResponse}
 * @param policyValue {PolicyValueResponse}
 */
function mapping_policy_for_bumnan_ready(calRes, policyValue) {
  return {
    yearEndPolicyValues: Array.from(policyValue.basic.table).map((/** @type {Record<string, string>} */ policy) => {
      return {
        age: { value: tryToNumber(policy.age) },
        yearEndPolicy: policy.year,
        monthEndPolicy: policy.month,
        annualBasicPremium: tryToNumber(policy.basic_premium),
        annualRiderPremium: tryToNumber(policy.rider_premium),
        annualTotalPremium: tryToNumber(policy.total_premium),
        cumulativeBasicPremium: tryToNumber(policy.cumulative_basic_premium),
        surrenderCash: tryToNumber(policy.surrender_cash),
        deathBenefit: tryToNumber(policy.death_benefit),
        pensionPerYear: tryToNumber(policy.pension_amount),
        cumulativePersionAmount: tryToNumber(policy.cumulative_pension_amount),
        pensionTotal: tryToNumber(policy.pension_total),
      }
    }),
    subtotalAnnualBasicPremium: tryToNumber(policyValue.basic.summary_basic_premium),
    subtotalAnnualRiderPremium: tryToNumber(policyValue.basic.summary_rider_premium),
    grandTotalPremium: tryToNumber(policyValue.basic.summary_total_premium),
    subtotalPensionAmount: tryToNumber(policyValue.basic.summary_pension_amount),
    subtotalLowSavingCashDrop: tryToNumber(policyValue.basic.summary_low_saving_cash_drop),
    subtotalMediumSavingCashDrop: tryToNumber(policyValue.basic.summary_medium_saving_cash_drop),
    subtotalHighSavingCashDrop: tryToNumber(policyValue.basic.summary_high_saving_cash_drop),
  }
}

/**
 * mapping_policy_for_mrta
 * @param calRes {CalculateResponse}
 * @param policyValue {PolicyValueResponse}
 */
function mapping_policy_for_mrta(calRes, policyValue) {
  return {
    yearEndPolicyValues: Array.from(policyValue.basic.table).map((/** @type {Record<string, string>} */ policy) => {
      return {
        yearEndPolicy: policy.year,
        basicSumAssured: tryToNumber(policy.basic_sum_assured),
        riderSumAssured: tryToNumber(policy.rider_sum_assured),
        surrenderCash: tryToNumber(policy.surrender_cash),
      }
    }),
  }
}

/**
 * mapping_policy_12PL
 * @param calRes {CalculateResponse}
 * @param policyValue {PolicyValueResponse}
 */
function mapping_policy_12PL(calRes, policyValue) {
  const output = {
    yearEndPolicyValues: Array.from(policyValue.basic.table).map((/** @type {Record<string, string>} */ policy) => {
      return {
        age: { value: tryToNumber(policy.age) },
        yearEndPolicy: policy.year,
        annualBasicPremium: tryToNumber(policy.basic_premium),
        annualRiderPremium: tryToNumber(policy.rider_premium),
        annualTotalPremium: tryToNumber(policy.total_premium),
        cumulativeBasicPremium: tryToNumber(policy.cumulative_basic_premium),
        cashDrop: tryToNumber(policy.cash_drop),
        sumCashDrop: tryToNumber(policy.sum_cash_drop),
        surrenderCash: tryToNumber(policy.surrender_cash),
        deathBenefit: tryToNumber(policy.death_benefit),
        totalBenefit: tryToNumber(policy.total_benefit),
        cumulativeDeathBenefit: tryToNumber(policy.cumulative_death_benefit),
        lowSavingCashDrop: tryToNumber(policy.low_saving_cash_drop),
        mediumSavingCashDrop: tryToNumber(policy.medium_saving_cash_drop),
        highSavingCashDrop: tryToNumber(policy.high_saving_cash_drop),
        lowInterest: tryToNumber(policy.low_interest),
        lowInterestDeposit: tryToNumber(policy.low_interest_deposit),
        midInterest: tryToNumber(policy.mid_interest),
        midInterestDeposit: tryToNumber(policy.mid_interest_deposit),
        highInterest: tryToNumber(policy.high_interest),
        highInterestDeposit: tryToNumber(policy.high_interest_deposit),
        totalLowDividend: tryToNumber(policy.total_low_dividend),
        totalMidDividend: tryToNumber(policy.total_mid_dividend),
        totalHighDividend: tryToNumber(policy.total_high_dividend),
      }
    }),
    subtotalAnnualBasicPremium: tryToNumber(policyValue.basic.summary_basic_premium),
    subtotalAnnualRiderPremium: tryToNumber(policyValue.basic.summary_rider_premium),
    grandTotalPremium: tryToNumber(policyValue.basic.summary_total_premium),
    subtotalGuaranteeDividend: tryToNumber(policyValue.basic.summary_cash_drop),
    subTotalNonGuaranteeDividendDeposit: tryToNumber(policyValue.basic.summary_sum_cash_drop),
    subTotalLowInterest: tryToNumber(policyValue.basic.summary_low_interest),
    subTotalLowInterestDeposit: tryToNumber(policyValue.basic.summary_low_interest_deposit),
    subTotalMidInterest: tryToNumber(policyValue.basic.summary_mid_interest),
    subTotalMidInterestDeposit: tryToNumber(policyValue.basic.summary_mid_interest_deposit),
    subTotalHighInterest: tryToNumber(policyValue.basic.summary_high_interest),
    subTotalHighInterestDeposit: tryToNumber(policyValue.basic.summary_high_interest_deposit),
    subTotalLowDividend: tryToNumber(policyValue.basic.summary_low_dividend),
    subTotalMidDividend: tryToNumber(policyValue.basic.summary_mid_dividend),
    subTotalHighDividend: tryToNumber(policyValue.basic.summary_high_dividend),
  }

  if (typeof policyValue.basic.summary_benefit_policy_table?.last_guarantee_dividend !== 'undefined') {
    output.yearEndPolicyValues[output.yearEndPolicyValues.length - 1].guaranteeDividend = tryToNumber(
      policyValue.basic.summary_benefit_policy_table.last_guarantee_dividend
    )
  }

  return output
}

function mapping_policy_for_noop(_calRes, _policyValue) {
  return {
    yearEndPolicyValues: [],
    subtotalAnnualBasicPremium: 0,
    subtotalAnnualRiderPremium: 0,
    grandTotalPremium: 0,
    subtotalPensionAmount: 0,
    subtotalLowSavingCashDrop: 0,
    subtotalMediumSavingCashDrop: 0,
    subtotalHighSavingCashDrop: 0,
  }
}

/**
 * mapping_benefit_summary
 * @param policyValue {PolicyValueResponse}
 */
function mapping_benefit_summary(policyValue) {
  const { benefit_summary_table } = policyValue.basic

  return {
    DEATH: {
      ILLNESS: tryToNumber(benefit_summary_table.DEATH_BY_ILLNESS),
      ACCIDENT: tryToNumber(benefit_summary_table.DEATH_BY_ACCIDENT),
      DISASTER: tryToNumber(benefit_summary_table.DEATH_BY_DISASTER),
      RIOT: tryToNumber(benefit_summary_table.DEATH_BY_RIOT),
      MATURITY: tryToNumber(benefit_summary_table.DEATH_BY_MATURITY),
    },
    CRITICAL_ILLNESS: {
      ILLNESS: tryToNumber(benefit_summary_table.CRITICAL_ILLNESS_BY_ILLNESS),
      ACCIDENT: tryToNumber(benefit_summary_table.CRITICAL_ILLNESS_BY_ACCIDENT),
      DISASTER: tryToNumber(benefit_summary_table.CRITICAL_ILLNESS_BY_DISASTER),
      RIOT: tryToNumber(benefit_summary_table.CRITICAL_ILLNESS_BY_RIOT),
      MATURITY: tryToNumber(benefit_summary_table.CRITICAL_ILLNESS_BY_MATURITY),
    },
    DISABILITY: {
      ILLNESS: tryToNumber(benefit_summary_table.DISABILITY_BY_ILLNESS),
      ACCIDENT: tryToNumber(benefit_summary_table.DISABILITY_BY_ACCIDENT),
      DISASTER: tryToNumber(benefit_summary_table.DISABILITY_BY_DISASTER),
      RIOT: tryToNumber(benefit_summary_table.DISABILITY_BY_RIOT),
      MATURITY: tryToNumber(benefit_summary_table.DISABILITY_BY_MATURITY),
    },
    BENEFIT_OF_MATURITY: {
      ILLNESS: tryToNumber(benefit_summary_table.BENEFIT_OF_MATURITY_BY_ILLNESS),
      ACCIDENT: tryToNumber(benefit_summary_table.BENEFIT_OF_MATURITY_BY_ACCIDENT),
      DISASTER: tryToNumber(benefit_summary_table.BENEFIT_OF_MATURITY_BY_DISASTER),
      RIOT: tryToNumber(benefit_summary_table.BENEFIT_OF_MATURITY_BY_RIOT),
      MATURITY: tryToNumber(benefit_summary_table.BENEFIT_OF_MATURITY_BY_MATURITY),
    },
  }
}

function mapping_benefit_summary_noop(_policyValue) {
  return {
    DEATH: {
      ILLNESS: 0,
      ACCIDENT: 0,
      DISASTER: 0,
      RIOT: 0,
      MATURITY: 0,
    },
    CRITICAL_ILLNESS: {
      ILLNESS: 0,
      ACCIDENT: 0,
      DISASTER: 0,
      RIOT: 0,
      MATURITY: 0,
    },
    DISABILITY: {
      ILLNESS: 0,
      ACCIDENT: 0,
      DISASTER: 0,
      RIOT: 0,
      MATURITY: 0,
    },
    BENEFIT_OF_MATURITY: {
      ILLNESS: 0,
      ACCIDENT: 0,
      DISASTER: 0,
      RIOT: 0,
      MATURITY: 0,
    },
  }
}

/**
 * mapping_tax_deduction
 * @param policyValue {PolicyValueResponse}
 */
function mapping_tax_deduction(policyValue) {
  return {
    taxDeductionBasicPremiumForYear: tryToNumber(policyValue.basic.tax_deduction_basic_premium_for_year),
    taxDeductionRiderPremuimForYear: tryToNumber(policyValue.basic.tax_deduction_rider_premium_for_year),
    taxDeductionTotalForYear: tryToNumber(policyValue.basic.tax_deduction_total_premium_for_year),
    taxDeductionRetirementForYear: tryToNumber(policyValue.basic.tax_deduction_retirement_for_year),
    taxDeductionLifeForYear: tryToNumber(policyValue.basic.tax_deduction_life_for_year),
    taxDeductionHealthForYear: tryToNumber(policyValue.basic.tax_deduction_health_for_year),
  }
}

function mapping_tax_deduction_noop(_policyValue) {
  return {
    taxDeductionBasicPremiumForYear: 0,
    taxDeductionRiderPremuimForYear: 0,
    taxDeductionTotalForYear: 0,
    taxDeductionRetirementForYear: 0,
    taxDeductionLifeForYear: 0,
    taxDeductionHealthForYear: 0,
  }
}

/**
 * mapping_expected_return_for_rpudr
 * @param policyValue {PolicyValueResponse}
 */
function mapping_expected_return_for_rpudr(policyValue) {
  return policyValue.basic.table.map((item) => {
    return {
      year: item.year,
      age: item.age,
      accumulatedTotalPremium: tryToNumber(item.accumulatedTotalPremium),
      benefitByExpectedReturn: Object.entries(item.benefitByExpectedReturn)
        .map(([key, value]) => ({
          [tryToNumber(key)]: {
            accountValueAfterBonus: tryToNumber(value.accountValueAfterBonus),
            deathBenefit: tryToNumber(value.deathBenefit),
          },
        }))
        .reduce((prev, acc) => ({ ...prev, ...acc }), {}),
    }
  })
}

/**
 * mapping_expected_return_for_spul
 * @param policyValue {PolicyValueResponse}
 */
function mapping_expected_return_for_spul(policyValue) {
  return policyValue.basic.table.map((item) => {
    return {
      year: item.year,
      month: item.month,
      age: item.age,
      benefitByExpectedReturn: {
        '2': {
          premium: {
            regularPremium: toNumber(item.benefitByExpectedReturn['2'].premium.regularPremium),
            totalPremium: toNumber(item.benefitByExpectedReturn['2'].premium.totalPremium),
            lumpSum: toNumber(item.benefitByExpectedReturn['2'].premium.lumpSum),
          },
          policyCharges: {
            premiumCharge: toNumber(item.benefitByExpectedReturn['2'].policyCharges.premiumCharge),
            lumpsumCharge: toNumber(item.benefitByExpectedReturn['2'].policyCharges.lumpsumCharge),
            chargeOfInsurance: toNumber(item.benefitByExpectedReturn['2'].policyCharges.chargeOfInsurance),
            adminCharge: toNumber(item.benefitByExpectedReturn['2'].policyCharges.adminCharge),
          },
          investment: {
            accountValueAfterDeduction: toNumber(
              item.benefitByExpectedReturn['2'].investment.accountValueAfterDeduction
            ),
            fundReturn: toNumber(item.benefitByExpectedReturn['2'].investment.fundReturn),
            accountValueBeforeBonus: toNumber(item.benefitByExpectedReturn['2'].investment.accountValueBeforeBonus),
            bonus: toNumber(item.benefitByExpectedReturn['2'].investment.bonus),
            accountValueAfterBonus: toNumber(item.benefitByExpectedReturn['2'].investment.accountValueAfterBonus),
          },
          surrenderCash: toNumber(item.benefitByExpectedReturn['2'].surrenderCash),
          deathBenefit: toNumber(item.benefitByExpectedReturn['2'].deathBenefit),
          accountValueAfterBonus: toNumber(item.benefitByExpectedReturn['2'].accountValueAfterBonus),
        },
        '-1': {
          accountValueAfterBonus: toNumber(item.benefitByExpectedReturn['-1'].accountValueAfterBonus),
        },
        '5': {
          accountValueAfterBonus: toNumber(item.benefitByExpectedReturn['5'].accountValueAfterBonus),
        },
      },
    }
  })
}

/**
 * policy_basic
 * @type {Record<'DEFAULT' | string, {
 *     [mapping_policy]: (CalculateResponse, PolicyValueResponse) => any,
 *     [mapping_benefit_summary]: (PolicyValueResponse) => any,
 *     [mapping_tax_deduction]: (PolicyValueResponse) => any,
 *     [mapping_investment_benefit]: (PolicyValueResponse) => any,
 *     [mapping_expected_return]: (CalculatePayload, CalculateResponse, PolicyValueResponse) => any
 *   }>}
 */
export const policy_basic = {
  // LifeProtect
  // PerfectLife
  // PLB
  // 12PL
  // S7
  // life ready
  // iShield
  // iCare
  DEFAULT: {
    mapping_policy: mapping_policy_for_default,
    mapping_benefit_summary,
    mapping_tax_deduction,
  },
  '12PL': {
    mapping_policy: mapping_policy_12PL,
  },
  // index linked
  E25G5A: {
    mapping_policy: mapping_policy_for_index_linked,
    mapping_benefit_summary,
    mapping_tax_deduction,
    mapping_investment_benefit: mapping_investment_benefit_for_index_linked,
  },
  E25G5AS: {
    mapping_policy: mapping_policy_for_index_linked,
    mapping_benefit_summary,
    mapping_tax_deduction,
    mapping_investment_benefit: mapping_investment_benefit_for_index_linked,
  },
  E15G8A: {
    mapping_policy: mapping_policy_for_index_linked,
    mapping_benefit_summary,
    mapping_tax_deduction,
    mapping_investment_benefit: mapping_investment_benefit_for_index_linked,
  },
  E15G8AS: {
    mapping_policy: mapping_policy_for_index_linked,
    mapping_benefit_summary,
    mapping_tax_deduction,
    mapping_investment_benefit: mapping_investment_benefit_for_index_linked,
  },
  // ultimateGrowth
  E20F10: { mapping_investment_benefit: mapping_investment_benefit_for_default },
  E25F15: { mapping_investment_benefit: mapping_investment_benefit_for_default },
  E25F25: { mapping_investment_benefit: mapping_investment_benefit_for_default },
  // LifePlus+ Saver
  '20P10': { mapping_investment_benefit: mapping_investment_benefit_for_default },
  '20P10S': { mapping_investment_benefit: mapping_investment_benefit_for_default },
  '25P15': { mapping_investment_benefit: mapping_investment_benefit_for_default },
  '25P15S': { mapping_investment_benefit: mapping_investment_benefit_for_default },
  // bumnan-ready
  R08F: { mapping_policy: mapping_policy_for_bumnan_ready },
  R60F: { mapping_policy: mapping_policy_for_bumnan_ready },
  // Life Retire 5
  LR05: { mapping_policy: mapping_policy_for_bumnan_ready },
  // investment
  RPUDR: {
    mapping_policy: mapping_policy_for_noop,
    mapping_benefit_summary: mapping_benefit_summary_noop,
    mapping_tax_deduction: mapping_tax_deduction_noop,
    mapping_expected_return: mapping_expected_return_for_rpudr,
  },
  SPUL: {
    mapping_policy: (calRes, policyValue) => {
      return {
        coverageDeath: tryToNumber(policyValue.basic.minimum_death_benefit),
        yearEndPolicyValues: [],
        subtotalAnnualBasicPremium: 0,
        subtotalAnnualRiderPremium: 0,
        grandTotalPremium: 0,
        subtotalPensionAmount: 0,
        subtotalLowSavingCashDrop: 0,
        subtotalMediumSavingCashDrop: 0,
        subtotalHighSavingCashDrop: 0,
      }
    },
    mapping_benefit_summary: mapping_benefit_summary_noop,
    mapping_tax_deduction: mapping_tax_deduction_noop,
    mapping_expected_return: mapping_expected_return_for_spul,
  },
  RPUL: {
    mapping_policy: mapping_policy_for_noop,
    mapping_benefit_summary: mapping_benefit_summary_noop,
    mapping_tax_deduction: mapping_tax_deduction_noop,
    mapping_expected_return: mapping_expected_return_for_rpudr,
  },
  MRTA_PLUS: {
    mapping_policy: mapping_policy_for_mrta,
  },
  MRTA_FULL: {
    mapping_policy: mapping_policy_for_mrta,
  },
  MRTA_TRUNCATE: {
    mapping_policy: mapping_policy_for_mrta,
  },
  GLTSP: {
    mapping_policy: mapping_policy_for_mrta,
  },
}
